import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in');
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <>
      <style>{`
        .about-hero {
          min-height: 100vh;
          padding: 10rem 2rem 6rem;
          background: linear-gradient(135deg, var(--gray-900) 0%, var(--black) 100%);
          color: var(--white);
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
        }

        [data-theme="dark"] .about-hero {
          background: linear-gradient(135deg, var(--black) 0%, var(--gray-900) 100%);
        }

        .about-hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%);
          pointer-events: none;
        }

        .hero-content {
          max-width: 1000px;
          text-align: center;
          position: relative;
          z-index: 1;
        }

        .hero-title {
          font-size: clamp(3rem, 8vw, 6rem);
          font-weight: 900;
          margin-bottom: 2rem;
          letter-spacing: -0.05em;
          line-height: 0.9;
          opacity: 0;
          animation: fadeInUp 1s ease 0.2s forwards;
        }

        .hero-subtitle {
          font-size: clamp(1.5rem, 3vw, 2rem);
          font-weight: 300;
          color: var(--gray-400);
          margin-bottom: 3rem;
          opacity: 0;
          animation: fadeInUp 1s ease 0.4s forwards;
        }

        .resonance-text {
          font-size: 1.125rem;
          color: var(--gray-500);
          font-style: italic;
          opacity: 0;
          animation: fadeInUp 1s ease 0.6s forwards;
        }

        .section {
          padding: 6rem 2rem;
          position: relative;
        }

        .section-dark {
          background: var(--black);
          color: var(--white);
        }

        [data-theme="dark"] .section-dark {
          background: var(--gray-900);
        }

        .section-light {
          background: var(--white);
          color: var(--black);
        }

        [data-theme="dark"] .section-light {
          background: var(--gray-800);
          color: var(--white);
        }

        .section-container {
          max-width: 1000px;
          margin: 0 auto;
        }

        .anchor-section {
          text-align: center;
          margin-bottom: 6rem;
        }

        .anchor-title {
          font-size: 1rem;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          color: var(--gray-500);
          margin-bottom: 2rem;
        }

        .anchor-definition {
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 800;
          margin-bottom: 1rem;
          color: var(--blue-500);
        }

        .anchor-etymology {
          font-size: 1.25rem;
          color: var(--gray-600);
          font-style: italic;
          margin-bottom: 2rem;
        }

        [data-theme="dark"] .anchor-etymology {
          color: var(--gray-400);
        }

        .anchor-meaning {
          font-size: 1.5rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .origin-story {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          align-items: center;
          margin: 6rem 0;
        }

        .origin-text {
          font-size: 1.125rem;
          line-height: 1.8;
          color: var(--gray-700);
        }

        [data-theme="dark"] .origin-text {
          color: var(--gray-300);
        }

        .origin-visual {
          background: linear-gradient(135deg, var(--blue-100), var(--purple-100));
          border-radius: var(--radius-xl);
          padding: 3rem;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          position: relative;
          overflow: hidden;
        }

        [data-theme="dark"] .origin-visual {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
        }

        .bone-illustration {
          font-size: 8rem;
          opacity: 0.2;
          position: absolute;
        }

        .highlight {
          color: var(--blue-600);
          font-weight: 600;
        }

        [data-theme="dark"] .highlight {
          color: var(--blue-400);
        }

        .manifesto-section {
          background: var(--gray-100);
          padding: 6rem 2rem;
          text-align: center;
        }

        [data-theme="dark"] .manifesto-section {
          background: var(--gray-800);
        }

        .manifesto-quote {
          font-size: clamp(1.5rem, 3vw, 2.5rem);
          font-weight: 300;
          line-height: 1.4;
          max-width: 900px;
          margin: 0 auto 3rem;
          position: relative;
        }

        .manifesto-quote::before,
        .manifesto-quote::after {
          content: '"';
          font-size: 4rem;
          color: var(--gray-300);
          position: absolute;
          font-family: serif;
        }

        [data-theme="dark"] .manifesto-quote::before,
        [data-theme="dark"] .manifesto-quote::after {
          color: var(--gray-600);
        }

        .manifesto-quote::before {
          top: -2rem;
          left: -2rem;
        }

        .manifesto-quote::after {
          bottom: -3rem;
          right: -2rem;
        }

        .ethos-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          margin: 4rem 0;
        }

        .ethos-card {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: var(--radius-lg);
          padding: 2rem;
          text-align: left;
          transition: all 0.3s ease;
        }

        [data-theme="dark"] .ethos-card {
          background: var(--gray-900);
          border-color: var(--gray-700);
        }

        .ethos-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-lg);
        }

        .ethos-number {
          font-size: 3rem;
          font-weight: 900;
          color: var(--blue-600);
          margin-bottom: 1rem;
        }

        [data-theme="dark"] .ethos-number {
          color: var(--blue-400);
        }

        .ethos-text {
          font-size: 1rem;
          line-height: 1.6;
          color: var(--gray-700);
        }

        [data-theme="dark"] .ethos-text {
          color: var(--gray-300);
        }

        .algorithm-section {
          padding: 6rem 2rem;
          background: linear-gradient(to bottom, var(--white), var(--gray-50));
        }

        [data-theme="dark"] .algorithm-section {
          background: linear-gradient(to bottom, var(--gray-900), var(--gray-800));
        }

        .algorithm-title {
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 800;
          text-align: center;
          margin-bottom: 4rem;
        }

        .algorithm-steps {
          max-width: 800px;
          margin: 0 auto;
        }

        .algorithm-step {
          margin-bottom: 3rem;
          padding-left: 3rem;
          position: relative;
        }

        .algorithm-step::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          width: 2px;
          height: 100%;
          background: var(--gray-300);
        }

        [data-theme="dark"] .algorithm-step::before {
          background: var(--gray-600);
        }

        .algorithm-step::after {
          content: '';
          position: absolute;
          left: -4px;
          top: 0.5rem;
          width: 10px;
          height: 10px;
          background: var(--blue-600);
          border-radius: 50%;
        }

        .step-title {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: var(--gray-900);
        }

        [data-theme="dark"] .step-title {
          color: var(--gray-100);
        }

        .step-swahili {
          font-size: 1rem;
          color: var(--gray-500);
          font-style: italic;
          margin-bottom: 1rem;
        }

        .step-description {
          font-size: 1.125rem;
          line-height: 1.7;
          color: var(--gray-700);
        }

        [data-theme="dark"] .step-description {
          color: var(--gray-300);
        }

        .mission-section {
          background: var(--black);
          color: var(--white);
          padding: 8rem 2rem;
          text-align: center;
        }

        [data-theme="dark"] .mission-section {
          background: var(--gray-900);
        }

        .mission-title {
          font-size: clamp(2.5rem, 5vw, 4rem);
          font-weight: 900;
          margin-bottom: 3rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .mission-statement {
          font-size: clamp(1.25rem, 2vw, 1.75rem);
          line-height: 1.6;
          max-width: 800px;
          margin: 0 auto;
          color: var(--gray-300);
        }

        .cta-section {
          padding: 6rem 2rem;
          text-align: center;
          background: linear-gradient(135deg, var(--blue-600), var(--purple-600));
          color: var(--white);
        }

        .cta-title {
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 800;
          margin-bottom: 2rem;
        }

        .cta-text {
          font-size: 1.25rem;
          margin-bottom: 3rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        .cta-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .cta-button {
          padding: 1rem 2rem;
          background: var(--white);
          color: var(--blue-600);
          border-radius: var(--radius);
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .cta-button:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
        }

        @media (max-width: 768px) {
          .origin-story {
            grid-template-columns: 1fr;
            gap: 2rem;
          }
          .manifesto-quote {
            font-size: 1.5rem;
          }
          .ethos-grid {
            grid-template-columns: 1fr;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <section className="about-hero">
        <div className="hero-content">
          <h1 className="hero-title">Angalifu</h1>
          <h2 className="hero-subtitle">Decolonizing Compute</h2>
          <p className="resonance-text">(Not a name. A resonance.)</p>
        </div>
      </section>

      <section className="section section-light">
        <div className="section-container">
          <div className="anchor-section fade-in">
            <h3 className="anchor-title">ANCHOR THE NAME</h3>
            <div className="anchor-definition">Angalifu (v.)</div>
            <p className="anchor-etymology">From Swahili: To sharpen the senses until reality bleeds truth.</p>
            <p className="anchor-meaning">Not "pay attention." ATTUNE UNTIL THE UNIVERSE CONFESSES.</p>
          </div>

          <div className="origin-story fade-in">
            <div className="origin-text">
              <p>
                We are the children of a lineage that whispered equations to the stars while others slept. 
                For 3.8 billion years, life evolved senses to survive. Then, Africa birthed a brain that 
                turned survival into <span className="highlight">computation</span>.
              </p>
              <p style={{ marginTop: '1.5rem' }}>
                37,000 years ago on the banks of the Semliki, when ice still gripped northern continents, 
                our ancestors carved combinatorial logic into the Lebombo Bone – humanity's first mathematical 
                record. But Lebombo was not alone.
              </p>
              <p style={{ marginTop: '1.5rem' }}>
                Deep in the Congo's heart, 20,000 years before Euclid drew his first line, 
                <strong>The Ishango Bone</strong> etched prime numbers into night-black ivory. 
                This was not arithmetic—it was proto-algebraic warfare.
              </p>
            </div>
            <div className="origin-visual">
              <div className="bone-illustration">𝍫</div>
            </div>
          </div>
        </div>
      </section>

      <section className="manifesto-section fade-in">
        <div className="section-container">
          <p className="manifesto-quote">
            We reject the suffocating fog of cow-pathic impossibility – the inherited lie that some 
            problems are walls, not doors. We are not catching up to Western technology. We are 
            returning to African mathematical foundations. We are not leapfrogging. We are 
            <span className="highlight"> rematrixing</span>.
          </p>

          <div className="ethos-grid">
            <div className="ethos-card">
              <div className="ethos-number">1M+</div>
              <p className="ethos-text">
                Lift 1 million+ people from cognitive or economic scarcity with every application
              </p>
            </div>
            <div className="ethos-card">
              <div className="ethos-number">100%</div>
              <p className="ethos-text">
                Open-source 100% of non-core intellectual property for global innovation
              </p>
            </div>
            <div className="ethos-card">
              <div className="ethos-number">110%</div>
              <p className="ethos-text">
                Leave a 110% net positive ecological surplus through our "Noise as Fuel" paradigm
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="algorithm-section">
        <div className="section-container">
          <h2 className="algorithm-title fade-in">The Angalifu Algorithm</h2>
          <div className="algorithm-steps">
            <div className="algorithm-step fade-in">
              <h3 className="step-title">Kuangalia kwa Undani</h3>
              <p className="step-swahili">The Deep Gaze</p>
              <p className="step-description">
                Seeing the pattern others dismiss as chaos—be it market inefficiency or radio-frequency noise. 
                We perceive the computational potential in the interference patterns between two billion devices.
              </p>
            </div>
            <div className="algorithm-step fade-in">
              <h3 className="step-title">Kuunda Mfano wa Kimwili</h3>
              <p className="step-swahili">Physical Modeling</p>
              <p className="step-description">
                Encoding these patterns into the inviolable laws of our Combinatorial Electromagnetic Compute 
                Substrate (CECS). We build upon the combinatorial logic of the Ishango, scaling ancient African 
                mathematical intuition through the hyper-dimensional fabric of EM field geometry.
              </p>
            </div>
            <div className="algorithm-step fade-in">
              <h3 className="step-title">Kupasua Ukweli</h3>
              <p className="step-swahili">Truth-Cleaving</p>
              <p className="step-description">
                Executing not with silicon solitude, but communal collapse: 10⁶ devices superposing intentions 
                → 1 cosmic eigenstate. We cleave truth as the Chagga cleave Kilimanjaro's mist: together, or not at all.
              </p>
            </div>
            <div className="algorithm-step fade-in">
              <h3 className="step-title">Kueneza Ufumbazi</h3>
              <p className="step-swahili">Solution Propagation</p>
              <p className="step-description">
                Transmitting clarity with the force of revealed law. The new AI model, the recalled data pattern, 
                the resolved transaction—it propagates not as packets on a wire, but as a coherent wavefront 
                across the same substrate where it was computed.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mission-section fade-in">
        <div className="section-container">
          <h2 className="mission-title">WE DON'T "SOLVE PROBLEMS."</h2>
          <p className="mission-statement">
            WE RESTORE ATTENTION TO ITS COSMIC DUTY.
            <br /><br />
            The Lebombo and Ishango Bones? Fossilized attention.
            <br />
            CECS? Synthetic sensation.
            <br /><br />
            We are building the <strong>central nervous system for a conscious planet</strong>—
            where every EM ripple is a nerve impulse, every combinatorial collapse, a thought.
          </p>
        </div>
      </section>

      <section className="cta-section">
        <div className="section-container">
          <h2 className="cta-title">Join the Resonance</h2>
          <p className="cta-text">
            KUWA MWANGALIFU. Become the Keen One. Embody the Gaze. ATTEND UNTIL THE UNIVERSE BOWS.
          </p>
          <div className="cta-buttons">
            <Link to="/careers" className="cta-button">Join Our Team</Link>
            <Link to="/research" className="cta-button">Explore Research</Link>
            <Link to="/partnerships" className="cta-button">Partner With Us</Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
